import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

const STATUSES = ['proposed', 'active', 'paused', 'done'];

export default function ProjectNew() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [users, setUsers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [form, setForm] = useState({
    name: '', description: '', segment_id: '', owner_id: '',
    status: 'proposed', start_date: '', deadline: '',
    budget_dollars: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.get('/api/users'), api.get('/api/segments')])
      .then(([{ users }, { segments }]) => { setUsers(users); setSegments(segments); })
      .catch(e => setErr(e.message));
    if (editing) {
      api.get(`/api/projects/${id}`).then(({ project }) => {
        setForm({
          name: project.name || '',
          description: project.description || '',
          segment_id: project.segment_id || '',
          owner_id: project.owner_id || '',
          status: project.status,
          start_date: project.start_date || '',
          deadline: project.deadline || '',
          budget_dollars: project.budget_cents != null ? (project.budget_cents / 100).toString() : '',
          notes: project.notes || '',
        });
      });
    }
  }, [id]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        segment_id: form.segment_id || null,
        owner_id: form.owner_id ? Number(form.owner_id) : null,
        status: form.status,
        start_date: form.start_date || null,
        deadline: form.deadline || null,
        budget_cents: form.budget_dollars ? Math.round(Number(form.budget_dollars) * 100) : null,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await api.patch(`/api/projects/${id}`, body);
        navigate(`/projects/${id}`);
      } else {
        const { id: newId } = await api.post('/api/projects', body);
        navigate(`/projects/${newId}`);
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-serif text-olive-900">{editing ? 'Edit project' : 'Add project'}</h1>

      <div className="card grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Name *</label>
          <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Build north pasture fence" />
        </div>
        <div className="col-span-2">
          <label className="label">Description</label>
          <textarea className="input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div>
          <label className="label">Segment</label>
          <select className="input" value={form.segment_id} onChange={e => set('segment_id', e.target.value)}>
            <option value="">—</option>
            {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Owner</label>
          <select className="input" value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
            <option value="">—</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Budget ($)</label>
          <input className="input" type="number" step="0.01" value={form.budget_dollars} onChange={e => set('budget_dollars', e.target.value)} placeholder="3500" />
        </div>
        <div>
          <label className="label">Start date</label>
          <input className="input" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
        </div>
        <div>
          <label className="label">Deadline</label>
          <input className="input" type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Notes</label>
          <textarea className="input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>

      {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

      <div className="flex gap-2">
        <button type="button" onClick={() => navigate('/projects')} className="btn-ghost">Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save' : 'Create'}</button>
      </div>
    </form>
  );
}
